import { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { UserPlus, Plus, Trash2, Loader2, ShieldCheck, AlertTriangle, Clock } from "lucide-react";
import { toast } from "sonner";

const CELL = {
  valid: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  expiring: "bg-amber-500/20 text-amber-700 dark:text-amber-300",
  expired: "bg-destructive/15 text-destructive",
  missing: "bg-secondary text-muted-foreground",
};

const cellLabel = (r) => {
  if (!r) return "Add";
  if (!r.expires_on) return "No expiry";
  return new Date(r.expires_on).toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
};

export default function Training() {
  const [data, setData] = useState(null);
  const [staffOpen, setStaffOpen] = useState(false);
  const [recOpen, setRecOpen] = useState(false);
  const [staff, setStaff] = useState({ name: "", role: "" });
  const [rec, setRec] = useState({ staff_id: "", course: "", completed_on: "", expires_on: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/training").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const addStaff = async () => {
    setSaving(true);
    try { await api.post("/training/staff", staff); toast.success("Staff member added"); setStaffOpen(false); setStaff({ name: "", role: "" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  const addRecord = async () => {
    setSaving(true);
    try {
      await api.post("/training/records", { ...rec, expires_on: rec.expires_on || null });
      toast.success("Training record saved"); setRecOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  const openRecord = (staffId, course = "") => { setRec({ staff_id: staffId, course, completed_on: "", expires_on: "" }); setRecOpen(true); };
  const removeStaff = async (s) => { await api.delete(`/training/staff/${s.id}`); toast.success("Removed"); load(); };

  if (!data) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  const latest = (s, course) => s.records.find((r) => r.course === course);
  const { summary } = data;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="training-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div>
          <span className="overline text-accent">Staff training log</span>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Mandatory training matrix</h1>
          <p className="text-muted-foreground mt-2">Core {data.regulator} courses per staff member with expiry tracking. Feeds your readiness score.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setStaffOpen(true)} data-testid="training-add-staff-button"><UserPlus className="w-4 h-4 mr-1.5" /> Add staff</Button>
          <Button onClick={() => openRecord(data.staff[0]?.id || "")} disabled={!data.staff.length} data-testid="training-add-record-button"><Plus className="w-4 h-4 mr-1.5" /> Add record</Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        {[
          { icon: ShieldCheck, label: "Core compliance", value: `${summary.compliance_pct}%`, sub: `${summary.valid}/${summary.required} records in date`, tone: "text-accent", id: "compliance" },
          { icon: Clock, label: "Expiring in 30 days", value: summary.expiring_30, sub: "renew soon", tone: "text-amber-500", id: "expiring" },
          { icon: AlertTriangle, label: "Expired", value: summary.expired, sub: "action needed", tone: "text-destructive", id: "expired" },
        ].map((t) => (
          <div key={t.id} className="bg-card border border-border rounded-2xl p-5 shadow-sm" data-testid={`training-tile-${t.id}`}>
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><t.icon className={`w-4 h-4 ${t.tone}`} /> {t.label}</div>
            <p className="font-display text-3xl font-extrabold mt-1">{t.value}</p>
            <p className="text-xs text-muted-foreground">{t.sub}</p>
          </div>
        ))}
      </div>

      {data.staff.length ? (
        <div className="bg-card border border-border rounded-2xl shadow-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="training-matrix">
            <thead className="bg-secondary/50 text-left">
              <tr>
                <th className="px-4 py-3 font-semibold sticky left-0 bg-secondary/50">Staff</th>
                {data.core_courses.map((c) => <th key={c} className="px-3 py-3 font-semibold text-xs whitespace-nowrap">{c}</th>)}
                <th className="px-3 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.staff.map((s) => (
                <tr key={s.id} data-testid={`training-staff-row-${s.id}`}>
                  <td className="px-4 py-3 sticky left-0 bg-card">
                    <p className="font-medium">{s.name}</p><p className="text-xs text-muted-foreground">{s.role}</p>
                  </td>
                  {data.core_courses.map((c) => {
                    const r = latest(s, c); const st = r ? r.status : "missing";
                    return (
                      <td key={c} className="px-2 py-2">
                        <button onClick={() => openRecord(s.id, c)} data-testid={`training-cell-${s.id}-${c.replace(/\W+/g, "-").toLowerCase()}`}
                          className={`w-full rounded-lg px-2 py-1.5 text-xs font-medium text-left ${CELL[st]} hover:opacity-80`}>
                          {cellLabel(r)}
                          <span className="block text-[10px] opacity-75 capitalize">{st}</span>
                        </button>
                      </td>
                    );
                  })}
                  <td className="px-2 py-2 text-right">
                    <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => removeStaff(s)} data-testid={`training-remove-staff-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-16 text-muted-foreground border border-dashed border-border rounded-2xl" data-testid="training-empty">
          <UserPlus className="w-8 h-8 mx-auto mb-2 opacity-50" /><p>Add your first staff member to start the matrix.</p>
        </div>
      )}

      <Dialog open={staffOpen} onOpenChange={setStaffOpen}>
        <DialogContent className="max-w-md" data-testid="training-staff-dialog">
          <DialogHeader><DialogTitle className="font-display">Add staff member</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name</Label><Input value={staff.name} onChange={(e) => setStaff({ ...staff, name: e.target.value })} className="mt-1.5" data-testid="training-staff-name" /></div>
            <div><Label>Role</Label><Input value={staff.role} onChange={(e) => setStaff({ ...staff, role: e.target.value })} placeholder="e.g. Senior support worker" className="mt-1.5" data-testid="training-staff-role" /></div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setStaffOpen(false)}>Cancel</Button>
            <Button onClick={addStaff} disabled={saving || staff.name.trim().length < 2} data-testid="training-staff-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={recOpen} onOpenChange={setRecOpen}>
        <DialogContent className="max-w-md" data-testid="training-record-dialog">
          <DialogHeader><DialogTitle className="font-display">Record training</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Staff member</Label>
              <Select value={rec.staff_id} onValueChange={(v) => setRec({ ...rec, staff_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="training-record-staff"><SelectValue placeholder="Choose" /></SelectTrigger>
                <SelectContent>{data.staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>Course</Label>
              <Select value={rec.course} onValueChange={(v) => setRec({ ...rec, course: v })}>
                <SelectTrigger className="mt-1.5" data-testid="training-record-course"><SelectValue placeholder="Choose" /></SelectTrigger>
                <SelectContent>{data.courses.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Completed on</Label><Input type="date" value={rec.completed_on} onChange={(e) => setRec({ ...rec, completed_on: e.target.value })} className="mt-1.5" data-testid="training-record-completed" /></div>
              <div><Label>Expires on</Label><Input type="date" value={rec.expires_on} onChange={(e) => setRec({ ...rec, expires_on: e.target.value })} className="mt-1.5" data-testid="training-record-expires" /></div>
            </div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setRecOpen(false)}>Cancel</Button>
            <Button onClick={addRecord} disabled={saving || !rec.staff_id || !rec.course || !rec.completed_on} data-testid="training-record-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
